import React, { useState } from "react";
import { CalendarDaysIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { getPortalLink, reactivateSubscription } from "../api/user";
import { toast } from "react-hot-toast";

const SubscriptionCancellingBanner = ({ compact = false }) => {
  const { organization, subscription, getProfile } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const [isReactivating, setIsReactivating] = useState(false);

  const paymentGateway = organization?.payment_gateway || "STRIPE";
  const isINR = organization?.payment_country === "IN";

  const cancellationDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : organization?.nextUsageResetAt
    ? new Date(organization.nextUsageResetAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const handleReactivate = async () => {
    if (paymentGateway === "STRIPE") {
      getPortalLink().then((res) => {
        window.open(res.url, "_blank");
      });
    } else {
      try {
        setIsReactivating(true);
        await reactivateSubscription();
        toast.success("Cancellation cleared. Your subscription will continue normally until the billing period ends.");
        if (getProfile) await getProfile();
      } catch (err) {
        toast.error(err?.message || "Failed to reactivate subscription");
      } finally {
        setIsReactivating(false);
      }
    }
  };

  // Only show if cancellation is scheduled but not yet executed
  if (
    !organization?.cancelledInitiatedAt ||
    organization?.subscriptionCancelled ||
    !isVisible
  ) {
    return null;
  }

  const actionLabel = isReactivating
    ? "Reactivating..."
    : isINR
    ? "Keep Subscription"
    : "Cancel Cancellation";

  if (compact) {
    return (
      <div className="mb-3 rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-orange-50/80 p-3 shadow-sm">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
            <CalendarDaysIcon className="h-4 w-4 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-amber-900">
              Cancellation Scheduled
            </p>
            <p className="mt-0.5 text-xs text-amber-700/90">
              {cancellationDate
                ? `Ends ${cancellationDate}`
                : "Ends at period end"}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button
                onClick={handleReactivate}
                disabled={isReactivating}
                className="inline-flex items-center rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-amber-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
              >
                {actionLabel}
              </button>
            </div>
          </div>
          <button
            onClick={() => setIsVisible(false)}
            className="-m-1 rounded-lg p-1 text-amber-600/70 transition-colors hover:bg-amber-200/50 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-br from-amber-50 to-orange-50/80 shadow-sm">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/15">
            <CalendarDaysIcon className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="font-semibold text-amber-900">
              Cancellation Scheduled
            </p>
            <p className="mt-0.5 text-sm text-amber-700/90">
              {cancellationDate
                ? `Your subscription will be cancelled on ${cancellationDate}`
                : "Your subscription will be cancelled at the end of your billing period"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={handleReactivate}
            disabled={isReactivating}
            className="inline-flex items-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-amber-700 hover:shadow focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-60"
          >
            {actionLabel}
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="rounded-lg p-2 text-amber-600/70 transition-colors hover:bg-amber-200/50 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCancellingBanner;
